import React, { useEffect, useRef } from 'react';
import { Modal, Form, Input, message, Button, Select, Space } from 'antd';
import { useTranslation } from '@refinedev/core';
import useAPI from '@/api/useApi';
import { getSectionContentHtml } from '@/Helpers/getSectionContentHtml';

const { TextArea } = Input;

interface HtmlEditorProps {
  value?: string;
  onChange?: (event: { target: { value: string } }) => void;
}

const HtmlEditor: React.FC<HtmlEditorProps> = ({ value = '', onChange }) => {
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  return (
    <div
      ref={editorRef}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      tabIndex={0}
      className="min-h-28 rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
      onInput={() =>
        onChange?.({ target: { value: editorRef.current?.innerHTML || '' } })
      }
    />
  );
};

interface EditSectionModalProps {
  open: boolean;
  onCancel: () => void;
  refetch: () => void;
  postId: string;
  sectionId: string;
  initialValues: {
    type: string;
    view: string;
    title: string;
    content: string | { html?: string } | null;
    quote: string;
    description: string;
    uploads: any[];
  };
}

export const EditSectionModal: React.FC<EditSectionModalProps> = ({
  open,
  onCancel,
  refetch,
  sectionId,
  initialValues,
}) => {
  const { translate } = useTranslation();
  const { usePatch } = useAPI();
  const [form] = Form.useForm();
  const watchedType = Form.useWatch('type', form);

  const { mutateAsync: updateSection, isLoading } = usePatch(
    `section/admin/${sectionId}`
  );

  useEffect(() => {
    if (open && initialValues) {
      form.setFieldsValue({
        type: initialValues.type,
        view: initialValues.view,
        title: initialValues.title,
        // EMBED stores { html }; form needs the raw HTML string
        content: getSectionContentHtml(initialValues.content),
        quote: initialValues.quote,
        description: initialValues.description,
      });
    }
  }, [open, initialValues, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        if (key !== 'uploads') {
          formData.append(key, values[key] ?? '');
        }
      });
      await updateSection(formData, {
        onSuccess: () => {
          message.success(translate('section.updated-successfully'));
          onCancel();
          refetch();
        },
        onError: () => {
          message.error(translate('section.error-updating'));
        },
      });
    } catch {
      // Validation failed
    }
  };

  return (
    <Modal
      title={translate('section.edit-section')}
      open={open}
      footer={null}
      onCancel={onCancel}
      width={800}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="type"
          label={translate('section.type')}
          rules={[
            { required: true, message: translate('pages.errors.required') },
          ]}
        >
          <Select>
            <Select.Option value="TEXT">
              {translate('section.text')}
            </Select.Option>
            <Select.Option value="MEDIA_DESCRIPTION">
              {translate('section.media-description')}
            </Select.Option>
            <Select.Option value="MEDIA_TEXT">
              {translate('section.media-text')}
            </Select.Option>
            <Select.Option value="MEDIA_QUOTE">
              {translate('section.media-quote')}
            </Select.Option>
            <Select.Option value="TWO_MEDIA">
              {translate('section.two-media')}
            </Select.Option>
            <Select.Option value="TEXT_QUOTE">
              {translate('section.text-quote')}
            </Select.Option>
            <Select.Option value="EMBED">
              {translate('section.embed')}
            </Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="view"
          label={translate('section.view')}
          rules={[
            { required: true, message: translate('pages.errors.required') },
          ]}
        >
          <Select>
            <Select.Option value="VERTICAL">VERTICAL</Select.Option>
            <Select.Option value="HORIZONTAL">HORIZONTAL</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="title"
          label={translate('section.title')}
          dependencies={['type']}
          rules={[
            {
              required: watchedType !== 'EMBED',
              message: translate('pages.errors.required'),
            },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="content"
          label={translate('section.content')}
          rules={[{ required: true }]}
          extra={
            watchedType === 'EMBED'
              ? translate('section.embed-content-hint')
              : undefined
          }
        >
          {watchedType === 'EMBED' ? <TextArea rows={4} /> : <HtmlEditor />}
        </Form.Item>

        <Form.Item name="quote" label={translate('section.quote')}>
          <HtmlEditor />
        </Form.Item>

        <Form.Item name="description" label={translate('section.description')}>
          <HtmlEditor />
        </Form.Item>

        <Space className="flex justify-end">
          <Button onClick={onCancel}>{translate('buttons.cancel')}</Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {translate('buttons.update')}
          </Button>
        </Space>
      </Form>
    </Modal>
  );
};
